// -----------------------------------------------------------------------
// <copyright company="Microsoft Corporation">
// Copyright (c) Microsoft Corporation. All rights reserved.
// </copyright>
// -----------------------------------------------------------------------

import type { ICommand, IResult } from '@ms/utilities-cross-window';
import type {
  ITokenRetriever,
  PlayState,
  RelativeTimestamp,
  SupportedPlaybackSpeed
} from '../types/external';
import type { IIframeApi, OnPreSessionErrorCallback } from '../types/internal';
import type { PlayerStats } from '../types/external/IPlaybackSessionBase';
import { onGetTokenCommand } from './commands/onGetTokenCommand';
import type { IAllPossibleNotifications, IHost } from './crossWindowChannel/hostWrapper';
import { ExternalPlayerApi } from './externalPlayerApi';
import { OnePlayerError } from './onePlayerError';
import { type IObservableProperty, ObservableProperty } from './observable';

type ValuesToTrackFromIframe = {
  playState: PlayState | undefined;
  currentTime: number | undefined;
  muted: boolean | undefined;
  volume: number | undefined;
  playbackSpeed: SupportedPlaybackSpeed | undefined;
};

/**
 * Adapter between TypeScript and the cross-window API for communication between the player/iframe and the
 * integrating app, with specific business logic needed for OnePlayer scenarios.
 */
export class IframeApi implements IIframeApi {
  public readonly externalPlayerApi: ExternalPlayerApi;
  public readonly playerStats: PlayerStats;

  public loadAndConnectToOnePlayerCallback!: (iframe: IIframeApi) => Promise<IIframeApi>;
  public crossWindow!: IHost;
  private readonly _tokenRetriever: ITokenRetriever;
  private readonly _embedUrl: string;
  private readonly _onError: OnPreSessionErrorCallback;
  private readonly _onTitleOrDisplayNameUpdate: (value: string | undefined) => void;
  private readonly _ttlMs: IObservableProperty<number | undefined> = new ObservableProperty<
    number | undefined
  >(undefined);
  private readonly _ttirMs: IObservableProperty<number | undefined> = new ObservableProperty<
    number | undefined
  >(undefined);
  private readonly _playerInteractiveTimestamp: IObservableProperty<RelativeTimestamp | undefined> =
    new ObservableProperty<RelativeTimestamp | undefined>(undefined);
  private readonly _pbsid: IObservableProperty<string | undefined> = new ObservableProperty<
    string | undefined
  >(undefined);
  // To prevent values being reflected right back to the iframe, keep track of the latest values we've heard from the iframe
  private readonly _latestValuesFromIframe: ValuesToTrackFromIframe = {
    playState: undefined,
    currentTime: undefined,
    muted: undefined,
    volume: undefined,
    playbackSpeed: undefined
  };

  public constructor(
    host: IHost,
    tokenRetriever: ITokenRetriever,
    embedUrl: string,
    onError: OnPreSessionErrorCallback,
    onTitleOrDisplayNameUpdate: (value: string | undefined) => void
  ) {
    this.crossWindow = host;
    this._tokenRetriever = tokenRetriever;
    this._embedUrl = embedUrl;
    this._onError = onError;
    this._onTitleOrDisplayNameUpdate = onTitleOrDisplayNameUpdate;
    this.externalPlayerApi = new ExternalPlayerApi();
    this.playerStats = {
      ttlMs: this._ttlMs,
      ttirMs: this._ttirMs,
      playerInteractiveTimestamp: this._playerInteractiveTimestamp,
      playbackSessionId: this._pbsid
    };
    this.subscribeToApiEvents();

    window.addEventListener('beforeunload', () => {
      this.externalPlayerApi.playerBeforeUnload.notify(true);
    });
  }

  /*
   * (SEND) Integrating app -> OnePlayer/iFrame
   * Integrating app asks OnePlayer/iFrame to begin warming data
   */
  public warmData(): Promise<void> {
    // Send command to iframe to trigger session.warmData
    return this.crossWindow
      .sendCommand({ command: 'one-player-warm-data' })
      .then(() => Promise.resolve())
      .catch((error) => {
        throw OnePlayerError.createGenericError('warning', 'warmData', 'warmDataUnknown', error);
      });
  }

  /*
   * (SEND) Integrating app -> OnePlayer/iFrame
   * Integrating app asks OnePlayer/iFrame to load the player
   */
  public loadPlayer(triggerPlayClick?: boolean | undefined): Promise<void> {
    // Send command to iframe to trigger session.loadPlayer
    return this.crossWindow
      .sendCommand({
        command: triggerPlayClick ? 'one-player-load-player-auto-play' : 'one-player-load-player'
      })
      .then(() => Promise.resolve())
      .catch((error) => {
        throw OnePlayerError.createGenericError('fatal', 'loadPlayer', 'loadPlayerUnknown', error);
      });
  }

  /*
   * (RECEIVE) OnePlayer/iFrame -> Integrating app
   * A command has been received from the player/iFrame requesting something from the integrating app.
   * It is treated as a promise as it requires the command to be completed unlike a fire and forget notification.
   */
  public onCommand(command: ICommand): Promise<IResult> | undefined {
    switch (command.command) {
      case 'getToken':
        return onGetTokenCommand(command, this._tokenRetriever, this._embedUrl);
    }

    // Returning `undefined` will implicitly map to the `unsupportedCommand` error code
    return undefined;
  }

  /*
   * (RECEIVE) OnePlayer/iFrame -> Integrating app
   * A notification has been received from the player/iFrame, pass this notification to the integrating app via the IPlaybackApi
   */
  public onNotification(notification: IAllPossibleNotifications): void {
    if (this._pbsid.value === undefined && notification.pbsid !== undefined) {
      this._pbsid.value = notification.pbsid;
    }
    /*
      Received message from iframe: {"notification":"one-player-play-state"}
      Received message from iframe: {"notification":"one-player-player-ready","isReady":false}
      Received message from iframe: {"notification":"one-player-playback-speed","playbackRate":1}
      Received message from iframe: {"notification":"one-player-volume","volume":1}
      Received message from iframe: {"notification":"one-player-muted","isMuted":false}
      Received message from iframe: {"notification":"one-player-current-time","currentTime":10}
      Received message from iframe: {"notification":"one-player-duration","durationInSeconds":50}
      Received message from iframe: {"notification":"one-player-playback-speed","playbackRate":1}
      Received message from iframe: {"notification":"one-player-play-state","playState":"Paused"}
      Received message from iframe: {"notification":"one-player-volume","volume":1}
      Received message from iframe: {"notification":"one-player-player-ready","isReady":true}
      Received message from iframe: {"notification":"success"}
      Received message from iframe: {"notification":"one-player-play-state","playState":"Playing"}
      Received message from iframe: {"notification":"one-player-play-state","playState":"Paused"}
    */
    switch (notification.notification) {
      case 'one-player-duration':
        this.externalPlayerApi.mediaDurationInSeconds.value = notification.durationInSeconds;
        break;
      case 'one-player-volume':
        this._latestValuesFromIframe.volume = notification.volume;
        this.externalPlayerApi.volume.value = notification.volume;
        break;
      case 'one-player-muted':
        this._latestValuesFromIframe.muted = notification.isMuted;
        this.externalPlayerApi.muted.value = notification.isMuted;
        break;
      case 'one-player-current-time':
        this._latestValuesFromIframe.currentTime = notification.currentTime;
        this.externalPlayerApi.currentTimeInSeconds.value = notification.currentTime;
        break;
      case 'one-player-playback-speed':
        this._latestValuesFromIframe.playbackSpeed = notification.playbackSpeed;
        this.externalPlayerApi.playbackSpeed.value = notification.playbackSpeed;
        break;
      case 'one-player-first-interactive':
        if (notification.timestamp !== undefined && notification.timestampTimeOrigin !== undefined) {
          this._playerInteractiveTimestamp.value = {
            timestamp: notification.timestamp,
            timeOrigin: notification.timestampTimeOrigin
          };
        }
        break;
      case 'one-player-player-ready':
        this.externalPlayerApi.isPlayerReady.value = notification.isReady === true;
        if (notification.ttlMs !== undefined) {
          this._ttlMs.value = notification.ttlMs;
        }
        break;
      case 'one-player-play-state':
        const newPlayStateFromIframe = notification.playState === 'Playing' ? 'Playing' : 'Paused';
        this._latestValuesFromIframe.playState = newPlayStateFromIframe;
        this.externalPlayerApi.playState.value = newPlayStateFromIframe;

        if (notification.ttirMs !== undefined) {
          this._ttirMs.value = notification.ttirMs;
        }
        break;
      case 'one-player-fatal-playback-error':
        this._onError(
          notification.onePlayerError ||
            OnePlayerError.createGenericError(
              'fatal',
              'playback',
              'playbackUnknown', // knownError
              notification.error,
              undefined, // message
              notification.pbsid,
              notification.hsi
            )
        );
        break;
      case 'one-player-fatal-create-session-error':
        this._onError(
          notification.onePlayerError ||
            OnePlayerError.createGenericError(
              'fatal',
              'createSession',
              'createSessionUnknown', // knownError
              notification.error,
              undefined, // message
              notification.pbsid,
              notification.hsi
            )
        );
        break;
      case 'one-player-reconnect-host':
        this.loadAndConnectToOnePlayerCallback(this).catch((error: unknown) => {
          throw OnePlayerError.createGenericError('fatal', 'requestReconnect', 'errorReconnecting', error);
        });
        break;
      case 'one-player-user-click-play':
        this.externalPlayerApi.lastUserAction.notify({ action: 'Play' });
        break;
      case 'one-player-user-click-pause':
        this.externalPlayerApi.lastUserAction.notify({ action: 'Pause' });
        break;
      case 'one-player-user-begin-seek':
        this.externalPlayerApi.lastUserAction.notify({ action: 'BeginSeek' });
        break;
      case 'one-player-user-end-seek':
        if (notification.targetTimeInSeconds === undefined) {
          this._onError(
            OnePlayerError.createGenericError(
              'warning',
              'notification',
              'targetTimeInSecondsUndefinedInEndSeekEvent',
              new Error('targetTimeInSeconds is undefined')
            )
          );
          return;
        }
        this.externalPlayerApi.lastUserAction.notify({
          action: 'EndSeek',
          targetTimeInSeconds: notification.targetTimeInSeconds
        });
        break;
      case 'one-player-user-skip':
        if (notification.targetTimeInSeconds === undefined) {
          this._onError(
            OnePlayerError.createGenericError(
              'warning',
              'notification',
              'targetTimeInSecondsUndefinedInSkipEvent',
              new Error('targetTimeInSeconds is undefined')
            )
          );
          return;
        }
        this.externalPlayerApi.lastUserAction.notify({
          action: 'Skip',
          targetTimeInSeconds: notification.targetTimeInSeconds
        });
        break;
      case 'one-player-title-updated':
        this._onTitleOrDisplayNameUpdate(notification.title);
        this.externalPlayerApi.title.value = notification.title;
        break;
      case 'one-player-item-url':
        this.externalPlayerApi.itemUrl.value = notification.itemUrl;
        break;
      case 'one-player-title-or-display-name-known':
        this._onTitleOrDisplayNameUpdate(notification.value);
        break;
    }
  }

  /*
   * (SEND) Integrating app -> OnePlayer/iFrame
   * Subscribe to iFrame API's PlaybackApi state changes and notify OnePlayer/iFrame via command
   */
  private subscribeToApiEvents(): void {
    this.externalPlayerApi.playState.subscribe((playState: PlayState | undefined) => {
      if (playState && playState !== this._latestValuesFromIframe.playState)
        this.crossWindow
          .sendCommand({ command: playState === 'Paused' ? 'one-player-pause' : 'one-player-play' })
          .catch((error: Error) => {
            this._onError(
              OnePlayerError.createGenericError(
                'warning',
                'playback',
                'enginePlayback', // knownError
                error,
                'could not send play/pause command' // message
              )
            );
          });
    });

    this.externalPlayerApi.currentTimeInSeconds.subscribe((currentTimeInSeconds: number | undefined) => {
      if (
        currentTimeInSeconds !== undefined &&
        currentTimeInSeconds !== this._latestValuesFromIframe.currentTime
      ) {
        this.crossWindow
          .sendCommand({ command: 'one-player-seek', position: currentTimeInSeconds })
          .catch((error: Error) => {
            this._onError(
              OnePlayerError.createGenericError(
                'warning',
                'playback',
                'enginePlayback', // knownError
                error,
                'could not send seek command' // message
              )
            );
          });
      }
    });

    this.externalPlayerApi.muted.subscribe((muted: boolean | undefined) => {
      if (muted !== this._latestValuesFromIframe.muted) {
        this.crossWindow
          .sendCommand({ command: muted ? 'one-player-mute' : 'one-player-unmute' })
          .catch((error: Error) => {
            this._onError(
              OnePlayerError.createGenericError(
                'warning',
                'playback',
                'enginePlayback', // knownError
                error,
                'could not send mute/unmute command' // message
              )
            );
          });
      }
    });

    this.externalPlayerApi.volume.subscribe((volume: number | undefined) => {
      if (volume !== this._latestValuesFromIframe.volume) {
        this.crossWindow
          .sendCommand({ command: 'one-player-set-volume', volumeLevel: volume })
          .catch((error: Error) => {
            this._onError(
              OnePlayerError.createGenericError(
                'warning',
                'playback',
                'enginePlayback', // knownError
                error,
                'could not send set-volume command' // message
              )
            );
          });
      }
    });

    this.externalPlayerApi.playbackSpeed.subscribe((playbackSpeed: SupportedPlaybackSpeed | undefined) => {
      if (playbackSpeed !== this._latestValuesFromIframe.playbackSpeed) {
        this.crossWindow
          .sendCommand({ command: 'one-player-set-playback-speed', playbackSpeed })
          .catch((error: Error) => {
            this._onError(
              OnePlayerError.createGenericError(
                'warning',
                'playback',
                'enginePlayback', // knownError
                error,
                'could not send set-playback-speed command' // message
              )
            );
          });
      }
    });
  }
}
